import { useState } from 'react';
import { useMaintenanceMode } from '../../../hooks/useMaintenanceMode';

export default function AdminMaintenance() {
  const { config, setEnabled, updateConfig, reset, loading } = useMaintenanceMode();
  const [showPreview, setShowPreview] = useState(false);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-brand-gray-400">
        <i className="ri-loader-4-line animate-spin text-3xl mb-3" />
        <p className="font-sans text-sm">Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status banner */}
      <div
        className={`rounded-2xl px-6 py-5 flex items-start gap-4 border ${
          config.enabled
            ? 'bg-red-50 border-red-200'
            : 'bg-brand-sage/10 border-brand-sage/20'
        }`}
      >
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            config.enabled ? 'bg-red-100 text-red-600' : 'bg-brand-sage/20 text-brand-black'
          }`}
        >
          <i className={config.enabled ? 'ri-alert-line' : 'ri-check-line'} />
        </div>
        <div>
          <p className="font-sans text-brand-black text-sm font-medium">
            {config.enabled
              ? 'El sitio está en mantenimiento — visible solo para administradores'
              : 'El sitio está activo y accesible para todos los visitantes'}
          </p>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">
            {config.enabled
              ? 'Cualquier persona que visite el sitio verá la página de mantenimiento excepto en /admin.'
              : 'Puedes activar el modo mantenimiento cuando necesites realizar cambios importantes.'}
          </p>
        </div>
      </div>

      {/* Toggle */}
      <div className="bg-white rounded-2xl border border-brand-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-brand-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <i className="ri-toggle-line text-brand-gray-400" />
            <div>
              <h3 className="font-sans text-brand-black text-sm font-medium">
                Modo mantenimiento
              </h3>
              <p className="font-sans text-brand-gray-400 text-xs mt-0.5">
                Activa o desactiva la página de mantenimiento para toda la web
              </p>
            </div>
          </div>
          <button
            onClick={() => setEnabled(!config.enabled)}
            className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
              config.enabled ? 'bg-red-500' : 'bg-brand-sage'
            }`}
            aria-label={config.enabled ? 'Desactivar mantenimiento' : 'Activar mantenimiento'}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform duration-300 ${
                config.enabled ? 'translate-x-7' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Message */}
        <div className="px-6 py-5 border-b border-brand-gray-100">
          <label className="font-sans text-brand-black text-xs uppercase tracking-wider font-medium">
            Mensaje para visitantes
          </label>
          <textarea
            value={config.message}
            onChange={(e) => updateConfig({ message: e.target.value })}
            rows={3}
            maxLength={300}
            className="mt-2 w-full border border-brand-gray-200 rounded-xl px-4 py-3 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
            placeholder="Describe brevemente por qué el sitio está en mantenimiento..."
          />
          <p className="font-sans text-brand-gray-400 text-xs mt-1.5 text-right">
            {config.message.length}/300
          </p>
        </div>

        {/* ETA */}
        <div className="px-6 py-5 border-b border-brand-gray-100">
          <label className="font-sans text-brand-black text-xs uppercase tracking-wider font-medium">
            Mensaje de vuelta estimada
          </label>
          <input
            type="text"
            value={config.eta}
            onChange={(e) => updateConfig({ eta: e.target.value })}
            maxLength={100}
            className="mt-2 w-full border border-brand-gray-200 rounded-xl px-4 py-3 font-sans text-sm focus:outline-none focus:border-brand-black"
            placeholder="Ej: Volvemos el lunes 12 de mayo."
          />
        </div>

        {/* Admin link toggle */}
        <div className="px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <i className="ri-shield-user-line text-brand-gray-400" />
            <div>
              <h3 className="font-sans text-brand-black text-sm font-medium">
                Enlace a administración
              </h3>
              <p className="font-sans text-brand-gray-400 text-xs mt-0.5">
                Muestra un botón de acceso al panel en la página de mantenimiento
              </p>
            </div>
          </div>
          <button
            onClick={() => updateConfig({ showAdminLink: !config.showAdminLink })}
            className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${
              config.showAdminLink ? 'bg-brand-sage' : 'bg-brand-gray-300'
            }`}
            aria-label={config.showAdminLink ? 'Ocultar enlace admin' : 'Mostrar enlace admin'}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300 ${
                config.showAdminLink ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={reset}
          className="flex items-center gap-1.5 font-sans text-brand-gray-600 text-sm px-4 py-2 rounded-lg border border-brand-gray-200 hover:border-brand-black hover:text-brand-black transition-colors"
        >
          <i className="ri-refresh-line" />
          Restaurar por defecto
        </button>
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-1.5 font-sans text-brand-gray-600 text-sm px-4 py-2 rounded-lg border border-brand-gray-200 hover:border-brand-black hover:text-brand-black transition-colors"
        >
          <i className={showPreview ? 'ri-eye-off-line' : 'ri-eye-line'} />
          {showPreview ? 'Ocultar vista previa' : 'Vista previa'}
        </button>
      </div>

      {/* Preview */}
      {showPreview && (
        <div className="rounded-2xl border border-brand-gray-200 overflow-hidden">
          <div className="bg-brand-black px-4 py-2 flex items-center gap-2">
            <span className="font-sans text-white/60 text-xs uppercase tracking-wider">
              Vista previa
            </span>
          </div>
          <div className="bg-brand-cream p-8 md:p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-brand-black flex items-center justify-center text-white mx-auto">
              <i className="ri-tools-line text-2xl" />
            </div>
            <h2 className="font-serif text-brand-black text-2xl md:text-3xl font-medium mt-6">
              En mantenimiento
            </h2>
            <p className="font-sans text-brand-gray-600 text-base mt-3 max-w-md mx-auto leading-relaxed">
              {config.message}
            </p>
            {config.eta && (
              <p className="font-sans text-brand-gray-400 text-sm mt-2 italic">
                {config.eta}
              </p>
            )}
            {config.showAdminLink && (
              <div className="mt-6 inline-flex items-center gap-2 bg-brand-black text-white font-sans text-sm tracking-widest uppercase px-6 py-2.5 rounded-full opacity-60">
                <i className="ri-shield-user-line" />
                Acceso administrador
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}